import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { companiesApi, type CompanyListResponse, type CompanySegments, type CompanyStats } from '@/lib/api'
import { useNotificationStore } from './notifications'

export interface Company {
  _id: string
  name: string
  domain?: string
  industry?: string
  size?: string
  website?: string
  phone?: string
  address?: {
    street?: string
    city?: string
    state?: string
    postalCode?: string
    country?: string
  }
  ownerId: string
  tags: string[]
  custom?: Record<string, any>
  orgId: string
  createdBy: string
  updatedBy: string
  createdAt: string
  updatedAt: string
  /** Only from `GET /companies?includeStats=true`; absent when not asked for or the lookup failed. */
  stats?: CompanyStats
}

export interface CompaniesState {
  companies: Company[]
  loading: boolean
  error: string | null
  meta: {
    page: number
    pageSize: number
    total: number
    hasNext: boolean
    /** Only with `includeStats=true`. */
    segments?: CompanySegments
  } | null
}

export const useCompaniesStore = defineStore('companies', () => {
  const companies = ref<Company[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const meta = ref<CompaniesState['meta']>(null)
  
  const notifications = useNotificationStore()

  // Computed
  const companiesCount = computed(() => companies.value.length)

  const companiesByOwner = computed(() => {
    const grouped: Record<string, Company[]> = {}
    companies.value.forEach(company => {
      if (!grouped[company.ownerId]) {
        grouped[company.ownerId] = []
      }
      grouped[company.ownerId]!.push(company)
    })
    return grouped
  })

  const companiesByIndustry = computed(() => {
    const grouped: Record<string, Company[]> = {}
    companies.value.forEach(company => {
      const industry = company.industry || 'Unknown'
      if (!grouped[industry]) {
        grouped[industry] = []
      }
      grouped[industry].push(company)
    })
    return grouped
  })

  // Actions
  // Latest request wins: a quick segment / search / page change can leave an older, slower list
  // response arriving last, which must not overwrite the newer list. A replace bumps the sequence;
  // an append (load more) belongs to the current list and is dropped if one replaced it.
  let listSeq = 0
  async function fetchCompanies(params?: any, append = false): Promise<void> {
    const seq = append ? listSeq : ++listSeq
    loading.value = true
    error.value = null

    try {
      const response: CompanyListResponse<Company> = await companiesApi.list(params)
      if (seq !== listSeq) return
      if (append) {
        companies.value = [...companies.value, ...response.data]
      } else {
        companies.value = response.data
      }
      meta.value = response.meta
    } catch (err: any) {
      if (seq !== listSeq) return
      error.value = err.response?.data?.error?.message || 'Failed to fetch companies'
      notifications.error('Failed to load companies')
    } finally {
      if (seq === listSeq) loading.value = false
    }
  }

  async function fetchCompany(id: string): Promise<Company | null> {
    loading.value = true
    error.value = null
    
    try {
      const response = await companiesApi.get(id)
      const company = response.data
      
      // Update company in store if it exists
      const index = companies.value.findIndex(c => c._id === id)
      if (index !== -1) {
        companies.value[index] = company
      } else {
        companies.value.push(company)
      }
      
      return company
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch company'
      notifications.error('Failed to load company')
      return null
    } finally {
      loading.value = false
    }
  }

  async function createCompany(companyData: Partial<Company>): Promise<Company | null> {
    loading.value = true
    error.value = null
    
    try {
      const response = await companiesApi.create(companyData)
      const newCompany = response.data
      companies.value.push(newCompany)
      notifications.success('Company created successfully')
      return newCompany
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to create company'
      notifications.error('Failed to create company')
      return null
    } finally {
      loading.value = false
    }
  }

  async function updateCompany(id: string, companyData: Partial<Company>): Promise<Company | null> {
    loading.value = true
    error.value = null
    
    try {
      const response = await companiesApi.update(id, companyData)
      const updatedCompany = response.data
      
      const index = companies.value.findIndex(c => c._id === id)
      if (index !== -1) {
        companies.value[index] = updatedCompany
      }
      
      notifications.success('Company updated successfully')
      return updatedCompany
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to update company'
      notifications.error('Failed to update company')
      return null
    } finally {
      loading.value = false
    }
  }

  async function deleteCompany(id: string): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      await companiesApi.delete(id)
      companies.value = companies.value.filter(c => c._id !== id)
      notifications.success('Company deleted successfully')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to delete company'
      notifications.error('Failed to delete company')
      return false
    } finally {
      loading.value = false
    }
  }

  function getCompanyById(id: string): Company | undefined {
    return companies.value.find(c => c._id === id)
  }

  function getCompanyName(id: string): string {
    const company = getCompanyById(id)
    return company?.name || 'Unknown Company'
  }

  function clearCompanies(): void {
    companies.value = []
    meta.value = null
    error.value = null
  }

  return {
    // State
    companies,
    loading,
    error,
    meta,
    
    // Computed
    companiesCount,
    companiesByOwner,
    companiesByIndustry,
    
    // Actions
    fetchCompanies,
    fetchCompany,
    createCompany,
    updateCompany,
    deleteCompany,
    getCompanyById,
    getCompanyName,
    clearCompanies,
  }
})